import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import styled from "styled-components";
import { FaEye, FaEyeSlash, FaRegClone } from "react-icons/fa";
import api from "../../services/api";
import theme from "../../styles/theme";

// The auth screens are not the teleprompter, so the small-window density rules
// do not apply here — this screen can breathe. It still draws only on the
// shared theme tokens so it stays part of the same system.

const Screen = styled.div`
  min-height: calc(100vh - var(--nav-h, 46px));
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.space.xl} ${theme.space.lg};
`;

const Card = styled.div`
  width: 100%;
  max-width: 380px;
  background: ${theme.color.surface};
  border: 1px solid ${theme.color.borderSubtle};
  border-radius: 12px;
  box-shadow: ${theme.shadow.panel};
  overflow: hidden;
`;

// A slim branded strip up top ties the card to the app's identity.
const Accent = styled.div`
  height: 4px;
  background: linear-gradient(
    90deg,
    ${theme.color.accent},
    ${theme.color.focus}
  );
`;

const Body = styled.div`
  padding: ${theme.space.xl};
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 1.25rem;
`;

const Mark = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 1rem;
  background: linear-gradient(
    135deg,
    ${theme.color.accent},
    ${theme.color.focus}
  );
`;

const BrandName = styled.span`
  font-size: ${theme.font.md};
  font-weight: 700;
  color: ${theme.color.text};
`;

const Title = styled.h1`
  font-size: 1.375rem;
  font-weight: 700;
  color: ${theme.color.text};
  margin-bottom: 0.25rem;
`;

const Subtitle = styled.p`
  font-size: ${theme.font.md};
  color: ${theme.color.textFaint};
  margin-bottom: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-size: ${theme.font.sm};
  font-weight: 500;
  color: ${theme.color.textMuted};
`;

const InputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  height: 42px;
  padding: 0 ${(props) => (props.$hasAdornment ? "2.5rem" : "0.75rem")} 0 0.75rem;
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.md};
  background: ${theme.color.surface};
  color: ${theme.color.text};
  font-family: inherit;
  font-size: ${theme.font.md};
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder {
    color: ${theme.color.textFaint};
  }

  &:focus {
    outline: none;
    border-color: ${theme.color.focus};
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
`;

const Reveal = styled.button`
  position: absolute;
  right: 0.25rem;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${theme.color.textFaint};
  cursor: pointer;
  border-radius: ${theme.radius.sm};

  &:hover {
    color: ${theme.color.text};
  }

  &:focus-visible {
    outline: 2px solid ${theme.color.focus};
    outline-offset: 1px;
  }
`;

const Submit = styled.button`
  height: 42px;
  margin-top: 0.25rem;
  border: none;
  border-radius: ${theme.radius.md};
  background: ${theme.color.accent};
  color: #ffffff;
  font-family: inherit;
  font-size: ${theme.font.md};
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover:not(:disabled) {
    background: ${theme.color.accentHover};
  }

  &:focus-visible {
    outline: 2px solid ${theme.color.focus};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Footer = styled.p`
  margin-top: 1.25rem;
  text-align: center;
  font-size: ${theme.font.sm};
  color: ${theme.color.textFaint};

  a {
    color: ${theme.color.focus};
    font-weight: 500;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      showToast("Email and password are required", "error");
      return;
    }

    const cleanData = {
      email: formData.email.trim(),
      password: formData.password,
    };

    try {
      setSubmitting(true);
      const response = await api.post("/auth/login", cleanData);

      if (!response.data.token) {
        throw new Error("No token received from server");
      }

      await login(response.data.token, response.data.user);
      showToast("Login successful", "success");
      navigate("/");
    } catch (error) {
      let errorMessage = "Failed to login. ";
      if (error.code === "ECONNABORTED") {
        errorMessage += "Request timed out. Please try again.";
      } else if (!error.response) {
        errorMessage += "Cannot connect to server.";
      } else {
        errorMessage +=
          error.response?.data?.message || "Please check your credentials.";
      }
      showToast(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Screen>
      <Card>
        <Accent />
        <Body>
          <Brand>
            <Mark>
              <FaRegClone />
            </Mark>
            <BrandName>Topic Manager</BrandName>
          </Brand>

          <Title>Welcome back</Title>
          <Subtitle>Sign in to your interview prep.</Subtitle>

          <Form onSubmit={handleSubmit}>
            <Field>
              Email
              <Input
                type="email"
                name="email"
                autoComplete="email"
                autoFocus
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Field>

            <Field>
              Password
              <InputWrap>
                <Input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  $hasAdornment
                  required
                />
                <Reveal
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </Reveal>
              </InputWrap>
            </Field>

            <Submit type="submit" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign in"}
            </Submit>
          </Form>

          <Footer>
            No account? <Link to="/register">Create one</Link>
          </Footer>
        </Body>
      </Card>
    </Screen>
  );
};

export default Login;
